import React, { useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import "../css/entry.css";
import "../css/reports.css";
import { useNavigate } from "react-router-dom";
import POReports from "../POReports";

function POSearchDetails() {
  const navigate = useNavigate();
  const printRef = useRef(null);

  const handleClose = () => {
    navigate(-1);
  };

  const handlePrint = () => {
    if (printRef.current) {
      const printWindow = window.open(
        "",
        "",
        "height=800,width=1000"
      );

      if (!printWindow) {
        return;
      }

      const styles = Array.from(document.styleSheets)
        .map((sheet) => {
          try {
            return Array.from(sheet.cssRules || [])
              .map((rule) => rule.cssText)
              .join("");
          } catch (e) {
            return "";
          }
        })
        .join("");

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Purchase Order Report</title>

            <style>
              ${styles}

              @page {
                size: A4 portrait;
                margin: 10mm;
              }

              html,
              body {
                margin: 0;
                padding: 0;
                width: 100%;
                background: #ffffff;
              }

              body {
                font-family: Arial, Helvetica, sans-serif;
                color: #000;
              }

              .print-content {
                width: 100%;
                max-width: 190mm;
                margin: 0 auto;
                box-sizing: border-box;
              }

              .a4-portrait {
                width: 100% !important;
                max-width: 190mm !important;
                min-width: 0 !important;
                padding: 0 !important;
                margin: 0 auto !important;
                box-sizing: border-box !important;
              }

              table {
                width: 100% !important;
                max-width: 100% !important;
                min-width: 0 !important;
                table-layout: fixed !important;
              }

              th,
              td {
                white-space: normal !important;
                overflow-wrap: anywhere !important;
                word-break: break-word !important;
              }

              .report_container {
                width: 100% !important;
                max-width: 100% !important;
                min-width: 0 !important;
              }

              .report_item {
                min-width: 0 !important;
                overflow-wrap: anywhere !important;
                word-break: break-word !important;
              }
            </style>
          </head>

          <body>
            <main class="print-content">
              ${printRef.current.innerHTML}
            </main>
          </body>
        </html>
      `);

      printWindow.document.close();

      printWindow.focus();

      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
        background: "#f5f9ff",
        p: {
          xs: 0.8,
          sm: 1.2,
          md: 1.8,
        },
      }}
    >
      {/* =====================================================
          MAIN CARD
      ====================================================== */}
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          borderRadius: {
            xs: 2,
            sm: 2.5,
            md: 3,
          },
          overflow: "hidden",
          border: "1px solid #d9e4f1",
          background: "#ffffff",
          boxShadow:
            "0 6px 24px rgba(13,71,161,0.08)",
        }}
      >
        {/* ===================================================
            HEADER
        ==================================================== */}
        <Box
          sx={{
            position: "relative",
            px: {
              xs: 1.5,
              sm: 2.5,
              md: 3,
            },
            py: {
              xs: 1.5,
              sm: 2,
              md: 2.3,
            },
            color: "#ffffff",
            background:
              "linear-gradient(135deg,#0d47a1 0%,#1565c0 48%,#1976d2 100%)",
            overflow: "hidden",

            "&::after": {
              content: '""',
              position: "absolute",
              width: {
                xs: 110,
                sm: 160,
                md: 220,
              },
              height: {
                xs: 110,
                sm: 160,
                md: 220,
              },
              right: {
                xs: -50,
                sm: -65,
                md: -85,
              },
              top: {
                xs: -60,
                sm: -80,
                md: -110,
              },
              borderRadius: "50%",
              background:
                "rgba(255,255,255,0.08)",
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={{
              xs: 1,
              sm: 1.4,
            }}
            sx={{
              position: "relative",
              zIndex: 1,
              minWidth: 0,
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: {
                  xs: 42,
                  sm: 48,
                  md: 52,
                },
                height: {
                  xs: 42,
                  sm: 48,
                  md: 52,
                },
                minWidth: {
                  xs: 42,
                  sm: 48,
                  md: 52,
                },
                borderRadius: {
                  xs: 1.8,
                  sm: 2.2,
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "rgba(255,255,255,0.16)",
                border:
                  "1px solid rgba(255,255,255,0.25)",
                backdropFilter: "blur(8px)",
                boxShadow:
                  "0 4px 14px rgba(0,0,0,0.12)",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: {
                    xs: "1rem",
                    sm: "1.15rem",
                    md: "1.3rem",
                  },
                }}
              >
                PO
              </Typography>
            </Box>

            {/* Title */}
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: {
                    xs: "1rem",
                    sm: "1.2rem",
                    md: "1.35rem",
                  },
                  lineHeight: 1.25,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                Purchase Order Report
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: {
                    xs: "0.68rem",
                    sm: "0.78rem",
                    md: "0.84rem",
                  },
                  opacity: 0.88,
                }}
              >
                View, print and review complete PO details
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* ===================================================
            CONTENT
        ==================================================== */}
        <CardContent
          sx={{
            p: {
              xs: 1,
              sm: 1.8,
              md: 2.5,
            },
            "&:last-child": {
              pb: {
                xs: 1,
                sm: 1.8,
                md: 2.5,
              },
            },
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          {/* =================================================
              REPORT AREA
          ================================================== */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              border: "1px solid #d9e4f1",
              borderRadius: {
                xs: 1.8,
                sm: 2,
                md: 2.5,
              },
              background: "#ffffff",
              overflow: "hidden",
              boxShadow:
                "0 2px 10px rgba(13,71,161,0.04)",
            }}
          >
            <Box
              ref={printRef}
              className="a4-portrait"
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                p: {
                  xs: 0.8,
                  sm: 1.2,
                  md: 2,
                },

                /* Prevent report content from
                   expanding the dashboard */
                overflowX: "hidden",

                "& *": {
                  boxSizing: "border-box",
                  maxWidth: "100%",
                },

                "& table": {
                  width: "100% !important",
                  maxWidth: "100% !important",
                  minWidth: "0 !important",
                  tableLayout:
                    "fixed !important",
                },

                "& th": {
                  whiteSpace:
                    "normal !important",
                  overflowWrap:
                    "anywhere !important",
                  wordBreak:
                    "break-word !important",
                },

                "& td": {
                  whiteSpace:
                    "normal !important",
                  overflowWrap:
                    "anywhere !important",
                  wordBreak:
                    "break-word !important",
                },

                "& .report_container": {
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                },

                "& .report_item": {
                  minWidth: 0,
                  maxWidth: "100%",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                },
              }}
            >
              <POReports />
            </Box>
          </Box>

          {/* =================================================
              ACTION BAR
          ================================================== */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              mt: {
                xs: 1.5,
                sm: 2,
                md: 2.5,
              },
              p: {
                xs: 1,
                sm: 1.3,
              },
              borderRadius: {
                xs: 1.8,
                sm: 2,
              },
              border: "1px solid #dce6f2",
              background:
                "linear-gradient(135deg,#f4f8ff 0%,#ffffff 100%)",
            }}
          >
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={{
                xs: 1,
                sm: 1.5,
              }}
              justifyContent="center"
              alignItems="stretch"
              sx={{
                width: "100%",
                maxWidth: "100%",
              }}
            >
              {/* PRINT */}
              <Button
                variant="contained"
                onClick={handlePrint}
                fullWidth
                sx={{
                  width: {
                    xs: "100%",
                    sm: "auto",
                  },
                  minWidth: {
                    sm: 150,
                  },
                  px: 2.5,
                  py: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: {
                    xs: "0.78rem",
                    sm: "0.84rem",
                  },
                  background:
                    "linear-gradient(135deg,#0d47a1,#1976d2)",
                  boxShadow:
                    "0 4px 12px rgba(13,71,161,0.20)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#08306b,#1565c0)",
                    boxShadow:
                      "0 6px 16px rgba(13,71,161,0.25)",
                  },
                }}
              >
                Print Data
              </Button>

              {/* CLOSE */}
              <Button
                variant="contained"
                onClick={handleClose}
                fullWidth
                sx={{
                  width: {
                    xs: "100%",
                    sm: "auto",
                  },
                  minWidth: {
                    sm: 150,
                  },
                  px: 2.5,
                  py: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: {
                    xs: "0.78rem",
                    sm: "0.84rem",
                  },
                  background:
                    "linear-gradient(135deg,#c62828,#e53935)",
                  boxShadow:
                    "0 4px 12px rgba(198,40,40,0.18)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#b71c1c,#c62828)",
                  },
                }}
              >
                Close
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default POSearchDetails;